import React, { useContext, useEffect, useState, useMemo } from "react";
import { Loader2, ChevronRight, ShieldCheck, ShieldAlert, Info, Sparkles, RotateCcw, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../store/auth-context";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import styles from "./ComConfigDashboard.module.css";
import { callComConfigApi } from "./callComConfigApi";

// Adjust the two context import paths above to match the real project
// structure — kept consistent with how activeStore/authCtx were used
// in the example (useContext-based, AuthContext exposing token + logout,
// StoreContext exposing activeStore.storeIdentifier).

const CRITICALITY_META = {
    CRITICAL: { label: "Critical", icon: ShieldAlert, color: "badge_critical" },
    IMPORTANT: { label: "Important", icon: ShieldCheck, color: "badge_important" },
    INFORMATIONAL: { label: "Informational", icon: Info, color: "badge_informational" },
};

const formatDomainLabel = (domain) => {
    if (!domain) return "";
    return domain
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const ComConfigDashboard = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const { activeStore: activeStoreJson } = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return activeStoreJson ? JSON.parse(activeStoreJson) : null; } catch { return null; } })();

    const [events, setEvents] = useState([]);
    const [policyMap, setPolicyMap] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [domainFilter, setDomainFilter] = useState(null);
    const [criticalityFilter, setCriticalityFilter] = useState(null);

    useEffect(() => {
        loadDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier]);

    const loadDashboard = async () => {
        if (!activeStore?.storeIdentifier || !authCtx?.token) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        const [eventsRes, policiesRes] = await Promise.all([
            callComConfigApi("comConfigGetEvents", { storeIdentifier: activeStore.storeIdentifier }, authCtx),
            callComConfigApi("comConfigGetResolvedPolicy", { storeIdentifier: activeStore.storeIdentifier }, authCtx),
        ]);

        if (eventsRes?.status === "success") {
            setEvents(Array.isArray(eventsRes.events) ? eventsRes.events : []);
        } else {
            setError("Couldn't load events. Try refreshing.");
        }

        if (policiesRes?.status === "success" && Array.isArray(policiesRes.policies)) {
            const map = {};
            policiesRes.policies.forEach((p) => {
                map[p.eventId] = p;
            });
            setPolicyMap(map);
        }

        setIsLoading(false);
    };

    const uniqueDomains = useMemo(() => {
        return [...new Set(events.map((e) => e.domain))].sort();
    }, [events]);

    const filteredGroupedByDomain = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const filtered = events.filter((event) => {
            if (domainFilter && event.domain !== domainFilter) return false;
            if (criticalityFilter && event.criticality !== criticalityFilter) return false;
            if (q) {
                return (
                    event.eventName.toLowerCase().includes(q) ||
                    event.eventCode.toLowerCase().includes(q)
                );
            }
            return true;
        });

        const groups = {};
        filtered.forEach((event) => {
            if (!groups[event.domain]) groups[event.domain] = [];
            groups[event.domain].push(event);
        });
        return groups;
    }, [events, searchQuery, domainFilter, criticalityFilter]);

    const hasActiveFilters = searchQuery.trim() || domainFilter || criticalityFilter;

    const clearFilters = () => {
        setSearchQuery("");
        setDomainFilter(null);
        setCriticalityFilter(null);
    };

    if (isLoading) {
        return (
            <div className={styles.stateContainer}>
                <Loader2 className={styles.spinner} size={22} />
                <span>Loading communication settings…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.stateContainer}>
                <ShieldAlert size={22} className={styles.errorIcon} />
                <span>{error}</span>
                <button className={styles.retryButton} onClick={loadDashboard}>
                    <RotateCcw size={14} />
                    Try again
                </button>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Communication settings</h1>
                <p className={styles.subtitle}>
                    How and where your customers, staff, and you get notified for every event in Lokaci Pro.
                </p>
            </div>

            <div className={styles.toolbar}>
                <div className={styles.searchWrapper}>
                    <Search size={15} className={styles.searchIcon} />
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search events…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className={styles.searchClear} onClick={() => setSearchQuery("")}>
                            <X size={13} />
                        </button>
                    )}
                </div>

                <div className={styles.filterRow}>
                    <div className={styles.filterGroup}>
                        {uniqueDomains.map((domain) => (
                            <button
                                key={domain}
                                className={domainFilter === domain ? styles.filterChipActive : styles.filterChip}
                                onClick={() => setDomainFilter(domainFilter === domain ? null : domain)}
                            >
                                {formatDomainLabel(domain)}
                            </button>
                        ))}
                    </div>

                    <div className={styles.filterDivider} />

                    <div className={styles.filterGroup}>
                        {Object.entries(CRITICALITY_META).map(([key, meta]) => (
                            <button
                                key={key}
                                className={criticalityFilter === key ? styles.filterChipActive : styles.filterChip}
                                onClick={() => setCriticalityFilter(criticalityFilter === key ? null : key)}
                            >
                                <meta.icon size={12} className={styles[meta.color]} />
                                {meta.label}
                            </button>
                        ))}
                    </div>

                    {hasActiveFilters && (
                        <button className={styles.clearAll} onClick={clearFilters}>
                            <X size={12} />
                            Clear all
                        </button>
                    )}
                </div>
            </div>

            {Object.keys(filteredGroupedByDomain).length === 0 ? (
                <div className={styles.emptyState}>
                    {events.length === 0 ? (
                        <>
                            <Sparkles size={20} />
                            <p>No events configured yet.</p>
                        </>
                    ) : (
                        <>
                            <Search size={20} />
                            <p>No events match your search.</p>
                            <button className={styles.retryButton} onClick={clearFilters}>
                                Clear filters
                            </button>
                        </>
                    )}
                </div>
            ) : (
                Object.keys(filteredGroupedByDomain).map((domain) => (
                    <div key={domain} className={styles.domainSection}>
                        <h2 className={styles.domainTitle}>{formatDomainLabel(domain)}</h2>
                        <div className={styles.eventList}>
                            {filteredGroupedByDomain[domain].map((event) => {
                                const policy = policyMap[event.ID];
                                const meta = CRITICALITY_META[event.criticality] || CRITICALITY_META.INFORMATIONAL;
                                const CriticalityIcon = meta.icon;
                                const isCustomized = policy?.source === "STORE_OVERRIDE";

                                return (
                                    <button
                                        key={event.ID}
                                        className={styles.eventRow}
                                        onClick={() => navigate(`/communication-settings/${event.ID}`)}
                                    >
                                        <div className={styles.eventRowLeft}>
                                            <CriticalityIcon size={16} className={styles[meta.color]} />
                                            <div>
                                                <div className={styles.eventName}>{event.eventName}</div>
                                                <div className={styles.eventCode}>{event.eventCode}</div>
                                            </div>
                                        </div>
                                        <div className={styles.eventRowRight}>
                                            <span className={isCustomized ? styles.statusCustomized : styles.statusDefault}>
                                                {isCustomized ? "Customized" : "Platform default"}
                                            </span>
                                            <ChevronRight size={16} className={styles.chevron} />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default ComConfigDashboard;